// Customer portal Invoices page (Phase 8, Session ATJ, M-9a). Every outstanding and paid invoice for
// the signed-in customer, read live via the ledger-provider seam (no caching drift). NOT a top-level
// nav section: the mobile bottom tab bar is a measured SEVEN-item ceiling (PortalShell.tsx's own
// header says an eighth "needs its own rethink"), so this is reached from the dashboard's own
// "Outstanding invoices" card via a "See all invoices" link, the same way ShowAllList reaches a
// fuller view from a dashboard section elsewhere in this portal. A lapsed session (401) sends the
// customer back to sign in.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PortalInvoiceListRow } from "../../lib/api";
import { PortalShell } from "./PortalShell";
import { FullScreenLoader } from "../../components/Loader";
import { ContactValue } from "../../components/ContactValue";

export function PortalInvoicesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PortalInvoiceListRow[] | null>(null);
  const [customerName, setCustomerName] = useState<string>("");
  const [error, setError] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([api.portal.invoices(), api.portal.me()])
      .then(([inv, me]) => {
        if (!live) return;
        setRows(inv.invoices);
        setCustomerName(me.customer.name);
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) navigate("/portal/login", { replace: true });
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [navigate]);

  async function logout() {
    try {
      await api.portal.logout();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  }

  async function payNow(id: string) {
    setPayingId(id);
    setPayError(null);
    try {
      const { payUrl } = await api.portal.payInvoice(id);
      window.open(payUrl, "_blank", "noopener,noreferrer");
    } catch {
      setPayError("Could not open the payment page. Please try again or call the office.");
    } finally {
      setPayingId(null);
    }
  }

  if (error) {
    return (
      <PortalShell userName={customerName || undefined} onSignOut={logout}>
        <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">Something went wrong</h1>
          <p className="text-ele-body">
            Please try again in a moment, or call the office on{" "}
            <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
          </p>
        </div>
      </PortalShell>
    );
  }
  if (!rows) return <FullScreenLoader />;

  const outstanding = rows.filter((r) => !r.paid);
  const paid = rows.filter((r) => r.paid);

  return (
    <PortalShell userName={customerName} onSignOut={logout}>
      <header className="mb-6">
        <h1 className="font-heading text-3xl tracking-wide text-ele-white">Invoices</h1>
        <p className="mt-1 text-ele-body">Your invoices, read live from our accounts.</p>
      </header>

      {payError && (
        <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-[rgba(255,255,255,0.04)] p-3 text-sm text-ele-white">
          {payError}
        </p>
      )}

      {rows.length === 0 ? (
        <p className="text-ele-body">You have no invoices on file.</p>
      ) : (
        <>
          <section className="mb-8">
            <h2 className="mb-3 font-heading text-lg text-ele-white">Outstanding</h2>
            {outstanding.length === 0 ? (
              <p className="text-sm text-ele-body">Nothing outstanding. Thank you.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {outstanding.map((inv) => (
                  <div key={inv.id} className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4">
                    <div className="flex items-baseline justify-between">
                      <span className="font-heading text-lg text-ele-white">{inv.amountDisplay}</span>
                      <span className="text-xs text-amber-400">{inv.statusLabel}</span>
                    </div>
                    {inv.invoiceNumber && <p className="text-sm text-ele-body">{inv.invoiceNumber}</p>}
                    {inv.dueDateDisplay && <p className="text-sm text-ele-body">Due {inv.dueDateDisplay}</p>}
                    {inv.canPayOnline && (
                      <button
                        type="button"
                        onClick={() => payNow(inv.id)}
                        disabled={payingId === inv.id}
                        className="mt-3 inline-block rounded-ele bg-ele-green px-4 py-2 font-heading text-sm tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-50"
                      >
                        {payingId === inv.id ? "Opening..." : "Pay now"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {paid.length > 0 && (
            <section>
              <h2 className="mb-3 font-heading text-lg text-ele-white">Paid</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {paid.map((inv) => (
                  <div key={inv.id} className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.02)] p-4 opacity-80">
                    <div className="flex items-baseline justify-between">
                      <span className="font-heading text-lg text-ele-white">{inv.amountDisplay}</span>
                      <span className="text-xs text-ele-green">{inv.statusLabel}</span>
                    </div>
                    {inv.invoiceNumber && <p className="text-sm text-ele-body">{inv.invoiceNumber}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </PortalShell>
  );
}
